import {
    ClientStatus,
    IAppointmentChecks,
    IArchivedClient,
    IClient,
    IClientBooking,
    InsuranceType,
    InsuranceYesOrNo,
    RelationshipToPrimaryInsured,
    UserSex,
} from "intake_shared/src/types";
import { normalizeWebformStep } from "intake_shared/src/util";
import moment from "../utils/app_moment";
import {
    getCollateralConsentAppointmentCheck,
    isCollateralConsentService,
} from "../utils/session_attendee_consent";
import { getFeeRevision } from "./feeRevision";
import { getStateFromAddress } from "../utils/utils";

export const normalizeStringArray = (
    value: string | string[] | undefined
): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value
            .map((entry) => (entry || "").trim())
            .filter((entry) => Boolean(entry));
    }
    const normalized = value.trim();
    return normalized ? [normalized] : [];
};

function normalizeSessionAttendeeDates(attendee: any) {
    return {
        ...attendee,
        created_at: attendee.created_at?.toDate
            ? attendee.created_at.toDate()
            : attendee.created_at,
        updated_at: attendee.updated_at?.toDate
            ? attendee.updated_at.toDate()
            : attendee.updated_at,
        consent_check: {
            ...(attendee.consent_check || {}),
            rechecked_at: attendee.consent_check?.rechecked_at?.toDate
                ? attendee.consent_check.rechecked_at.toDate()
                : attendee.consent_check?.rechecked_at ?? null,
            signed_at: attendee.consent_check?.signed_at?.toDate
                ? attendee.consent_check.signed_at.toDate()
                : attendee.consent_check?.signed_at ?? null,
            sent_at: attendee.consent_check?.sent_at?.toDate
                ? attendee.consent_check.sent_at.toDate()
                : attendee.consent_check?.sent_at ?? null,
            reminder_sent_at: attendee.consent_check?.reminder_sent_at?.toDate
                ? attendee.consent_check.reminder_sent_at.toDate()
                : attendee.consent_check?.reminder_sent_at ?? null,
        },
    };
}

function normalizeBookingSessionAttendees(booking: IClientBooking): IClientBooking {
    if (!booking.session_attendees) {
        return booking;
    }
    return {
        ...booking,
        session_attendees: booking.session_attendees.map(
            normalizeSessionAttendeeDates
        ),
    };
}

function normalizeStoreDate(value: unknown): Date | null {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (
        typeof value === "object" &&
        value !== null &&
        "toDate" in value &&
        typeof value.toDate === "function"
    ) {
        const date = value.toDate();
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    }
    return null;
}

function recomputeCollateralConsentAppointmentChecks(
    appointmentChecks: IAppointmentChecks,
    bookings: IClientBooking[]
): IAppointmentChecks {
    return bookings.reduce((updatedChecks, booking) => {
        if (!isCollateralConsentService(booking) || !booking.session_attendees) {
            return updatedChecks;
        }

        const existingAppointmentCheck = updatedChecks[booking.id];
        const rollupRecheckedAt =
            normalizeStoreDate(
                existingAppointmentCheck?.collateral_consent_rechecked
            ) || normalizeStoreDate(existingAppointmentCheck?.date_added);

        if (!rollupRecheckedAt) {
            return updatedChecks;
        }

        return {
            ...updatedChecks,
            [booking.id]: getCollateralConsentAppointmentCheck(
                existingAppointmentCheck,
                booking,
                booking.session_attendees || [],
                rollupRecheckedAt
            ),
        };
    }, appointmentChecks);
}

export function getClientFromStoreData(doc_data: any): IClient | IArchivedClient {
    const {
        id,
        email,
        phone_no,
        first_name,
        last_name,
        sex = "" as UserSex,
        city = "",
        pronouns = "",
        address,
        zip_code,
        insurance_provider,
        insurance_member_id,
        notes,
        client_services_notes = "",
        status,
        held_bookings,
        confirmed_bookings,
        waitlist_clinician_ids = [],
        waitlist_locations = [],
        waitlist_time_categories = [],
        waitlist_days = [],
        waitlist_wants_intern = false,
        complete_notes = { reason: "", notes: "" },
        accept_assignment = "" as InsuranceYesOrNo,
        waitlist_notes = "",
        primary_insured_name = "",
        primary_insured_address = "",
        primary_insured_state = "",
        primary_insured_city = "",
        primary_insured_zipcode = "",
        requested_time = "",
        requested_day = [],
        relationship_to_primary_insured = RelationshipToPrimaryInsured.SELF,
        insurance_plan_employer = "",
        group_number = "",
        inquiry_method,
        insurance_type = status === ClientStatus.INCOMPLETE_INQUIRY
            ? ("" as InsuranceType)
            : InsuranceType.GROUP_HEALTH_PLAN,
        date_updated,
        fees_update_details = {},
        requested_service = "",
        preferred_name = "",
        status_history = {},
        cancellation_notes = {},
        requested_therapist = [],
        assigned_to = "",
        secondary_assignee = "",
        on_waitlist = false,
        appointment_checks = {},
        appointment_history = [],
        archived_on,
        is_blacklisted = false,
        is_current_chart = false,
        old_ids = [],
        last_updated_by = "",
        referred_by = "",
        applicable_fee = {
            masters_fee: true,
            dr_fee: true,
            psych_fee: true,
            intern_fee: true,
            testing_fee: true,
            intern_extern_testing_fee: true,
            extern_fee: true,
            inn_masters_fee: true,
            gifted_assessment_fee: true,
            short_term_evaluation_documentation_fee: true,
        },
        hear_about_us = "",
        suggested_call_date_time = "",
        consent_to_sms = "",
        webform_selected_cta_option = "",
        webform_selected_cta_details = "",
        fees_updated_by = "",
        inquiry_id = "",
        primary_parent = "",
        primary_parent_last_name = "",
        primary_parent_email = "",
        primary_parent_phone = "",
        other_parent = "",
        other_parent_last_name = "",
        other_parent_email = "",
        other_parent_phone = "",
        has_no_other_parent_guardian = false,
        no_other_parent_guardian_reason = "",
        mode_of_communication = "",
        webform_status = "",
        webform_step,
        tracking_params = "",
        cookie_params = "",
        client_is_b2b = false,
        client_is_on_trial = false,
        intake_session_history = [],
        session_attendees_history = [],
        treatment_type = "",
        preferred_therapist_gender = [],
        presenting_concerns = [],
        consent_to_email = "",
        preferred_language_of_communication = [],
        requested_location = [],
        payment_card_count,
        tags,
        active_tag_keys,
    } = doc_data;
    const called_on = doc_data[ClientStatus.INQUIRY]?.called_on;
    const emailed_on = doc_data[ClientStatus.INQUIRY]?.emailed_on;
    const source = doc_data.source || "Humantold";
    const normalizedWebformStep = normalizeWebformStep(webform_step);
    const {
        consent_form_sent = "",
        consent_form_rechecked = "",
        consent_error_cc = "",
        consent_issue_cancel = "",
        credit_card_validated = "",
        client_added_to_amd = "",
        cancellation_notes: held_cancellation_notes = "",
    } = doc_data[ClientStatus.APPT_HELD] || {};

    const { last_verification_date, CalYr } =
        doc_data[ClientStatus.INS_VERIFICATION] || {};
    const normalizedHeldBookings = held_bookings
        .filter((h) => Boolean(h.id))
        .map(normalizeBookingSessionAttendees);
    const normalizedConfirmedBookings = (confirmed_bookings || []).map(
        normalizeBookingSessionAttendees
    );
    const normalizedAppointmentHistory = appointment_history.map((appt: any) => ({
        ...normalizeBookingSessionAttendees(appt),
        clinician_display_name: appt.clinician_display_name || appt.office || "",
    }));
    const normalizedAppointmentChecks = recomputeCollateralConsentAppointmentChecks(
        Object.keys(appointment_checks).reduce((prev, curr) => {
            const consent_form_sent = appointment_checks[curr].consent_form_sent
                ?.toDate
                ? appointment_checks[curr].consent_form_sent?.toDate()
                : null;
            const consent_form_rechecked = appointment_checks[curr]
                .consent_form_rechecked?.toDate
                ? appointment_checks[curr].consent_form_rechecked?.toDate()
                : null;
            const client_submitted_at = appointment_checks[curr].client_submitted_at
                ?.toDate
                ? appointment_checks[curr].client_submitted_at?.toDate()
                : null;
            const consent_form_reminder_sent = appointment_checks[curr]
                .consent_form_reminder_sent?.toDate
                ? appointment_checks[curr].consent_form_reminder_sent?.toDate()
                : null;
            const consent_error_cc = appointment_checks[curr].consent_error_cc
                ?.toDate
                ? appointment_checks[curr].consent_error_cc?.toDate()
                : null;
            const consent_issue_cancel = appointment_checks[curr]
                .consent_issue_cancel?.toDate
                ? appointment_checks[curr].consent_issue_cancel?.toDate()
                : null;
            const date_added = appointment_checks[curr].date_added?.toDate
                ? appointment_checks[curr].date_added?.toDate()
                : null;
            const credit_card_validated = appointment_checks[curr]
                .credit_card_validated?.toDate
                ? appointment_checks[curr].credit_card_validated?.toDate()
                : null;
            const client_added_to_amd = appointment_checks[curr].client_added_to_amd
                ?.toDate
                ? appointment_checks[curr].client_added_to_amd?.toDate()
                : null;
            const collateral_consent_rechecked = appointment_checks[curr]
                .collateral_consent_rechecked?.toDate
                ? appointment_checks[curr].collateral_consent_rechecked?.toDate()
                : null;
            return {
                ...prev,
                [curr]: {
                    ...appointment_checks[curr],
                    consent_form_sent,
                    consent_form_reminder_sent,
                    client_submitted_at,
                    consent_error_cc,
                    consent_form_rechecked,
                    consent_issue_cancel,
                    date_added,
                    credit_card_validated,
                    client_added_to_amd,
                    collateral_consent_rechecked,
                },
            };
        }, {} as IAppointmentChecks),
        [
            ...normalizedHeldBookings,
            ...normalizedConfirmedBookings,
            ...normalizedAppointmentHistory,
        ]
    );

    const client: IClient | IArchivedClient = {
        id,
        email,
        phone_no,
        first_name,
        sex,
        city,
        preferred_name,
        last_name,
        pronouns,
        address,
        state: doc_data.state || getStateFromAddress(doc_data.address) || "",
        date_of_birth: doc_data?.date_of_birth
            ? moment(doc_data?.date_of_birth?.toDate())?.toDate()
            : null,
        zip_code,
        insurance_provider,
        insurance_member_id,
        accept_assignment,
        insurance_plan_employer,
        group_number,
        notes,
        client_services_notes,
        status,
        fees_update_details,
        loaded_fee_revision: getFeeRevision(fees_update_details?.updated_at),
        held_bookings: normalizedHeldBookings,
        complete_notes,
        requested_time,
        requested_day: normalizeStringArray(requested_day),
        fees_updated_by,
        confirmed_bookings: normalizedConfirmedBookings,
        primary_insured_name,
        intake_session_history,
        session_attendees_history,
        primary_insured_dob: doc_data.primary_insured_dob
            ? doc_data.primary_insured_dob.toDate()
            : "",
        primary_insured_address,
        primary_insured_state,
        primary_insured_city,
        primary_insured_zipcode,
        relationship_to_primary_insured,
        waitlist_clinician_ids,
        waitlist_locations,
        insurance_type,
        source,
        waitlist_time_categories,
        waitlist_days,
        waitlist_wants_intern,
        waitlist_notes,
        [ClientStatus.INS_VERIFICATION]: {
            ...(doc_data[ClientStatus.INS_VERIFICATION] || {}),
            policy_effective_date:
                doc_data[ClientStatus.INS_VERIFICATION]?.policy_effective_date ||
                CalYr ||
                "",
            last_verification_date: last_verification_date
                ? last_verification_date.toDate()
                : "",
        },
        [ClientStatus.INCOMPLETE_INQUIRY]:
            doc_data[ClientStatus.INCOMPLETE_INQUIRY] || {},
        [ClientStatus.FEE_SETTING]: doc_data[ClientStatus.FEE_SETTING] || {},
        [ClientStatus.INS_FOLLOWUP]: doc_data[ClientStatus.INS_FOLLOWUP] || {},
        [ClientStatus.FOLLOWUP]: doc_data[ClientStatus.FOLLOWUP] || {},
        [ClientStatus.APPT_SETTING]: doc_data[ClientStatus.APPT_SETTING] || {},
        [ClientStatus.INQUIRY]: {
            called_on: called_on ? called_on.toDate() : "",
            emailed_on: emailed_on ? emailed_on.toDate() : "",
        },
        [ClientStatus.APPT_HELD]: {
            ...(doc_data[ClientStatus.APPT_HELD] || {}),
            consent_form_sent: consent_form_sent ? consent_form_sent.toDate() : "",
            consent_form_rechecked: consent_form_rechecked
                ? consent_form_rechecked.toDate()
                : "",
            consent_error_cc: consent_error_cc ? consent_error_cc.toDate() : "",
            consent_issue_cancel: consent_issue_cancel
                ? consent_issue_cancel.toDate()
                : "",
            credit_card_validated: credit_card_validated
                ? credit_card_validated.toDate()
                : "",
            client_added_to_amd: client_added_to_amd
                ? client_added_to_amd.toDate()
                : "",
            cancellation_notes: held_cancellation_notes || "",
        },
        inquiry_method,
        status_history,
        date_updated: date_updated.toDate(),
        requested_service,
        cancellation_notes,
        requested_therapist: normalizeStringArray(requested_therapist),
        assigned_to,
        secondary_assignee,
        on_waitlist,
        client_is_b2b,
        client_is_on_trial,
        clinician_fees: doc_data.clinician_fees || {},
        appointment_checks: normalizedAppointmentChecks,
        appointment_history: normalizedAppointmentHistory,
        archived_on: (archived_on && archived_on.toDate()) || "",
        is_blacklisted,
        is_current_chart,
        old_ids,
        last_updated_by,
        referred_by,
        applicable_fee,
        hear_about_us,
        suggested_call_date_time,
        consent_to_sms,
        webform_selected_cta_option,
        webform_selected_cta_details,
        primary_parent,
        primary_parent_last_name,
        primary_parent_email,
        primary_parent_phone,
        other_parent,
        other_parent_last_name,
        other_parent_email,
        other_parent_phone,
        has_no_other_parent_guardian,
        no_other_parent_guardian_reason,
        tracking_params,
        cookie_params,
        inquiry_id,
        mode_of_communication,
        webform_status,
        ...(normalizedWebformStep ? { webform_step: normalizedWebformStep } : {}),
        treatment_type,
        preferred_therapist_gender: normalizeStringArray(preferred_therapist_gender),
        presenting_concerns: normalizeStringArray(presenting_concerns),
        consent_to_email,
        preferred_language_of_communication: normalizeStringArray(
            preferred_language_of_communication
        ),
        requested_location: normalizeStringArray(requested_location),
        ...(payment_card_count !== undefined ? { payment_card_count } : {}),
    };
    if (tags !== undefined) client.tags = tags;
    if (active_tag_keys !== undefined) client.active_tag_keys = active_tag_keys;
    return client;
}

export function convertStoreDocToClient(doc: any): IClient | IArchivedClient {
    const doc_data = doc.data();
    if (!doc_data) {
        return {} as IClient | IArchivedClient;
    }
    return getClientFromStoreData(doc_data);
}
